import { storeMutation } from '@/store/storeMutations.constant';
import { storeAction } from '@/store/storeActions.constant';
import { storeGetters } from '@/store/storeGetters.constant';
import { wrapAction } from '@/common/utils/actionLifecycle';
import { getBasicHeaders, getRandomId } from '@/common/utils/utils';
import Olm from '@/plugins/olm/olm';
import matrixSdk, { WebStorageSessionStore } from 'matrix-js-sdk';
import { iamConstants } from '@/components/InAppMessaging/iam.constant';
import {
    isValidConversationId,
    createMatrixUserId,
    matrixUserIdLocalPart,
    isGroupConversation,
    unReadMessages,
    chatParticipantOneOnOne,
} from "../../components/InAppMessaging/iam.utils";
import axios from 'axios';

global.Olm = Olm;
const matrixServerUrl = process.env.VUE_APP_CHAT_API_URL;
const deviceId = localStorage.matrixDeviceId || (localStorage.matrixDeviceId = getRandomId());

function messengerState() {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        deviceId,
        rooms: [],
        joinRequestsSent: {},
        conversationMessageStates: {},
        messengerSessionState: 'NEW',
        messengerUsername: null,
        chatParticipantProfiles: {},
        failedMessages: {},
        globalChatNotificationCount: null,
    };
}


function reconcileChatParticipantProfiles(state, dispatch) {
    const participantIds = {};
    state.rooms.forEach((room) => {
        Object.keys(room.currentState.members).forEach((k) => {
            const roomMember = room.currentState.members[k];
            const userId = matrixUserIdLocalPart(roomMember.userId);
            participantIds[userId] = true;
        });
    });

    return dispatch(storeAction.fetchProfileData, Object.keys(participantIds));
}

function loadChatHistory(state, dispatch) {
    function loadRoomHistory(room) {
        return dispatch(storeAction.fetchConversationHistory, {
            conversationId: room.roomId,
            limit: 75,
        }).then(hasMore => !hasMore || loadRoomHistory(room));
    }

    const requests = [];
    state.rooms.forEach((room) => {
        if (room.oldState.paginationToken) {
            requests.push(loadRoomHistory(room));
        }
    });
    return Promise.all(requests);
}

function getRoomParticipants(state, room) {
    if (!state || !state.matrixClient || !room || !room.currentState) {
        return {};
    }

    const participants = {};
    Object.keys(room.currentState.members).forEach((k) => {
        const roomMember = room.currentState.members[k];
        if (roomMember.membership === 'leave') return;

        const { presence = null } = roomMember.user || {};
        const matrixUserId = roomMember.userId;
        const userId = matrixUserIdLocalPart(matrixUserId);

        const messengerUsername = state.messengerUsername && state.messengerUsername.toLowerCase();
        const hasPendingInvite =
            roomMember.membership === 'invite' &&
            (userId !== messengerUsername || !state.joinRequestsSent[room.roomId]);

        const profileData = state.chatParticipantProfiles[userId] || {};

        participants[userId] = {
            id: profileData.id || userId,
            matrixUserId: roomMember.userId,
            name: profileData.name || null,
            presence,
            profileImageUrl: profileData.profileImageUrl || null,
            fallbackProfileImageUrl: profileData.fallbackProfileImageUrl || null,
            primaryChapterName: profileData.primaryChapterName || null,
            profileDataLoadState: profileData.loadState || 'NEW',
            hasPendingInvite,
        };
    });

    return participants;
}

function createConversationEvent(matrixEvent) {
    const content = matrixEvent.getContent();
    const type = (() => {
        switch (matrixEvent.getType()) {
            case 'm.room.message':
                return 'message';
            default:
                return null;
        }
    })();

    const ourContent = (() => {
        switch (type) {
            case 'message':
                return {
                    messageText: content.body,
                };
            default:
                return {};
        }
    })();

    return {
        id: matrixEvent.getId(),
        type,
        author: matrixUserIdLocalPart(matrixEvent.sender.userId),
        ts: matrixEvent.getTs(),
        status: matrixEvent.event.status,
        error: matrixEvent.error,
        ...ourContent,
    };
}

function getEvents(state, room) {
    const events = [];

    const failedMessages = state.failedMessages[room.roomId] || {};
    let previousMessageId = null;
    room.timeline.forEach((matrixEvent) => {
        const failedHere = failedMessages[previousMessageId] || [];
        events.push(...failedHere);
        events.push(createConversationEvent(matrixEvent));
        previousMessageId = matrixEvent.getId();
    });

    events.sort((a, b) => a.ts - b.ts);

    return events;
}

function getGlobalChatNotification(state) {
    const conversations = state.rooms.map((room) => {
        const events = getEvents(state, room);
        return {
            room,
            events,
        };
    });

    const newChatNotifications = [];
    if (conversations && conversations.length) {
        conversations.forEach((convo) => {
            if (unReadMessages(convo).length) {
                newChatNotifications.push(convo);
            }
        });
    }
    return newChatNotifications;
}

function sendReadNotification(roomId, eventId, dispatch) {
    if (roomId && eventId) {
        dispatch(storeAction.setRoomReadMarkers, { roomId, eventId });
    }
}

const messengerMutations = {
    [storeMutation.createMatrixClient]: (state, { baseUrl, accessToken, userId }) => {
        state.matrixClient = matrixSdk.createClient({
            baseUrl,
            accessToken,
            userId,
            timelineSupport: true,
            deviceId: state.deviceId,
            sessionStore: new WebStorageSessionStore(global.sessionStorage),
        });
    },
    [storeMutation.setMessengerUsername]: (state, value) => {
        state.messengerUsername = value && value.toLowerCase();
    },
    [storeMutation.setMessengerSessionState]: (state, value) => {
        state.messengerSessionState = value;
    },
    [storeMutation.setRooms]: (state, value) => {
        state.rooms = value;

        //setting global notifications for chat
        const newNotifications = getGlobalChatNotification(state);
        if (newNotifications && newNotifications.length) {
            state.globalChatNotificationCount = newNotifications.length;
        } else {
            state.globalChatNotificationCount = null;
        }
    },
    [storeMutation.mergeChatParticipantProfile]: (state, chatUsers) => {
        state.chatParticipantProfiles = {
            ...state.chatParticipantProfiles,
            ...chatUsers,
        };
    },
    [storeMutation.setParticipantState]: (state, { userIds, loadState }) => {
        if (userIds && userIds.length) {
           userIds.forEach((userId) => {
               state.chatParticipantProfiles = {
                   ...state.chatParticipantProfiles,
                   [userId]: {
                       ...state.chatParticipantProfiles[userId],
                       loadState,
                   },
               };
           });
        }
    },
    [storeMutation.insertFailedMessage]: (state, { conversationId: cid, previousMessageId: pmid, message }) => {
        const fm = state.failedMessages;
        const forCnv = fm[cid] || {};
        const forPm = forCnv[pmid] || [];
        state.failedMessages = {
            ...fm,
            [cid]: {
                ...forCnv,
                [pmid]: [
                    ...forPm,
                    {
                        ...message,
                        isFailed: true,
                    },
                ],
            },
        };
    },
};

const messengerActions = {
    [storeAction.ensureMessengerSession]: wrapAction(
        storeAction.ensureMessengerSession,
        ({ state, commit, dispatch }) => {
            return Promise.all([
                dispatch(`memberProfile/${storeAction.getAccessToken}`, null, { root: true }),
                dispatch(`memberProfile/${storeAction.getAccessTokenDecoded}`, null, { root: true }),
            ])
                .then(([accessToken, tokenInfo]) => {
                    if (['PENDING', 'READY'].includes(state.messengerSessionState) || !accessToken || !tokenInfo) {
                        return;
                    }

                    commit(storeMutation.setMessengerSessionState, 'PENDING');
                    commit(storeMutation.setMessengerUsername, tokenInfo.payload.ypoId);

                    // This dummy client will be replaced after login
                    commit(storeMutation.createMatrixClient, { baseUrl: matrixServerUrl });
                    return (
                        state.matrixClient
                            .login('m.login.jwt', {
                                token: accessToken,
                                initial_device_display_name: iamConstants.DEVICE_DISPLAY_NAME,
                            })
                            .then((response) => {
                                commit(storeMutation.createMatrixClient, {
                                    baseUrl: matrixServerUrl,
                                    accessToken: response.access_token,
                                    userId: response.user_id,
                                });
                            })
                            // .then(() => {
                            //   return state.matrixClient.initCrypto();
                            // })
                            .then(() => {
                                state.matrixClient.startClient();
                                return new Promise(res => state.matrixClient.once('sync', res));
                            })
                            .then(() => {
                                const rooms = state.matrixClient.getVisibleRooms();
                                if (rooms && rooms.length) {
                                    const roomWithEmptyTimeline = rooms.find(room => !room.timeline.length);
                                    if (roomWithEmptyTimeline) {
                                        dispatch(storeAction.joinConversation, roomWithEmptyTimeline.roomId);
                                    }
                                }
                                commit(storeMutation.setRooms, state.matrixClient.getVisibleRooms());

                                loadChatHistory(state, dispatch);
                                return reconcileChatParticipantProfiles(state, dispatch);
                            })
                            .then(() => {
                                state.matrixClient.on('event', () => {
                                    const rooms = state.matrixClient.getVisibleRooms();
                                    if (rooms && rooms.length) {
                                        const roomWithEmptyTimeline = rooms.find(room => !room.timeline.length);
                                        if (roomWithEmptyTimeline) {
                                            dispatch(storeAction.joinConversation, roomWithEmptyTimeline.roomId);
                                        }
                                    }
                                    commit(storeMutation.setRooms, state.matrixClient.getVisibleRooms());
                                    reconcileChatParticipantProfiles(state, dispatch);
                                });

                                commit(storeMutation.setMessengerSessionState, 'READY');
                            })
                    );
                })
                .catch((err) => {
                    commit(storeMutation.setMessengerSessionState, 'FAILED');
                    throw err;
                });
        },
    ),
    [storeAction.persistConversation]: wrapAction(storeAction.persistConversation, ({ state }, conversation) => {
        const messengerUsername = state.messengerUsername && state.messengerUsername.toLowerCase();
        const otherParticipants = Object.keys(conversation.participants || {})
            .filter(k => k.toLowerCase() !== messengerUsername)
            .map(k => createMatrixUserId(k));

        return state.matrixClient
            .createRoom({
                visibility: 'private',
                invite: otherParticipants,
            })
            .then(({ room_id: r }) => r);
    }),
    [storeAction.createChatGroup]: wrapAction(storeAction.createChatGroup, ({ state, dispatch }, groupData) => {
        return dispatch(storeAction.connectToMembers, groupData)
            .then(() => {
                return state.matrixClient.createRoom({
                    visibility: 'private',
                    name: groupData.name,
                    invite: Object.keys(groupData.participants).map(k => createMatrixUserId(k)),
                });
            })
            .then((resp) => {
                return resp;
            });
    }),
    [storeAction.renameConversation]: wrapAction(storeAction.renameConversation, ({ state }, data) => {
       const { roomId, roomName } = data;
       state.matrixClient.setRoomName(roomId, roomName).then((resp) => { return resp; });
    }),
    [storeAction.inviteToConversation]: wrapAction(storeAction.inviteToConversation, ({ state, dispatch, rootGetters }, inviteData) => {
       const { roomId, memberId } = inviteData;
       const userId = createMatrixUserId(memberId);
       const currentUserId = rootGetters[`memberProfile/${storeGetters.currentUserId}`];

        if (!currentUserId) {
            throw new Error('Must be logged in to connect.');
        }

        dispatch(
            `memberProfile/${storeAction.addConnection}`,
            {
                personId: currentUserId,
                connectedPersonId: memberId,
                isConnected: true,
            },
            { root: true },
        )
        .then(() => {
            return state.matrixClient.invite(roomId, userId);
        })
        .then((resp) => { return resp; });
    }),
    [storeAction.removeFromConversation]: wrapAction(storeAction.removeFromConversation, ({ state }, data) => {
        const { roomId, memberId } = data;
        const userId = createMatrixUserId(memberId);
        state.matrixClient.kick(roomId, userId).then((resp) => { return resp; });
    }),
    [storeAction.joinConversation]: wrapAction(storeAction.joinConversation, ({ state }, roomId) => {
        if (state.joinRequestsSent[roomId]) return false;

        state.joinRequestsSent = {
            ...state.joinRequestsSent,
            [roomId]: true,
        };

        const joinOpts = {};
        state.matrixClient.joinRoom(roomId, joinOpts);
    }),
    [storeAction.deleteConversation]: wrapAction(storeAction.deleteConversation, ({ state }, { conversationId }) => {
        return state.matrixClient.leave(conversationId).then(() => {
            return state.matrixClient.forget(conversationId);
        });
    }),
    [storeAction.removeConnection]: wrapAction(
        storeAction.removeConnection,
        ({ dispatch, state }, conversation) => {
            const participant = chatParticipantOneOnOne(conversation, state.messengerUsername);
            return dispatch(`memberProfile/${storeAction.addConnection}`, {
                connectedPersonId: participant.id,
                isConnected: false,
            }, { root: true }).then(() => {
                return dispatch(storeAction.deleteConversation, { conversationId: conversation.id });
            });
        },
    ),
    [storeAction.connectToMembers]: ({ rootGetters, dispatch }, conversation) => {
        const currentUserId = rootGetters[`memberProfile/${storeGetters.currentUserId}`];

        if (!currentUserId) {
            throw new Error('Must be logged in to connect.');
        }

        const membersToConnect = Object.keys(conversation.participants || {})
            .filter(p => p && p !== currentUserId)
            .map(p => ({
                    personId: currentUserId,
                    connectedPersonId: conversation.participants[p].id,
                    isConnected: true,
            }));

        return dispatch(`memberProfile/${storeAction.addBulkConnections}`, membersToConnect, { root: true });
    },
    [storeAction.sendMessage]: wrapAction(
        storeAction.sendMessage,
        ({ state, dispatch, getters }, { conversation, messageText }) => {
            if (!isValidConversationId(conversation.id)) {
                const newParticipantIds = Object.keys(conversation.participants);
                const exConversation = getters[storeGetters.conversations].find((cnv) => {
                    const exParticipantIds = Object.keys(cnv.participants);
                    return (
                        !isGroupConversation(cnv) && newParticipantIds.length + 1 === exParticipantIds.length &&
                        newParticipantIds.every(pid => cnv.participants.hasOwnProperty(pid))
                    );
                });
                conversation = exConversation || conversation;
            }

            let conversationId = conversation.id;

            const cms = state.conversationMessageStates;
            if (cms[conversation.id] === 'SENDING') {
                throw new Error('This conversation is in SENDING state. Cannot send messages right now.');
            }
            cms[conversation.id] = 'SENDING';

            function sendMessage() {
                return state.matrixClient.sendEvent(conversationId, 'm.room.message', {
                    msgtype: 'm.text',
                    body: messageText,
                }).then((resp) => { return resp; });
            }

            const sent = isValidConversationId(conversationId)
                ? dispatch(storeAction.connectToMembers, conversation).then(() => sendMessage())
                : dispatch(storeAction.persistConversation, conversation)
                      .then((newConversationId) => {
                          conversationId = newConversationId;
                      })
                      .then(() => dispatch(storeAction.connectToMembers, conversation))
                      .then(() => sendMessage());

            return sent
                .then((resp) => {
                    //sending seen notification
                    sendReadNotification({
                        roomId: conversationId,
                        eventId: resp.event_id,
                        dispatch,
                    });
                    cms[conversation.id] = null; // Clear the sending state for both old and new ids
                    cms[conversationId] = null;
                })
                .catch((err) => {
                    // Could set a failure state instead in future
                    cms[conversation.id] = null;
                    cms[conversationId] = null;
                    throw err;
                })
                .then(() => conversationId);
        },
    ),
    [storeAction.setRoomReadMarkers]: wrapAction(
        storeAction.setRoomReadMarkers,
        ({ state }, { roomId, eventId }) => {
            return state.matrixClient.setRoomReadMarkers(roomId, eventId).then((resp) => { return resp; });
        },
    ),
    [storeAction.fetchConversationHistory]: wrapAction(
        storeAction.fetchConversationHistory,
        ({ state }, { conversationId, limit }) => {
            const room = state.rooms.find(r => r.roomId === conversationId);
            return state.matrixClient.paginateEventTimeline(room.getLiveTimeline(), { backwards: true, limit });
        },
    ),
    [storeAction.fetchProfileData]: wrapAction(storeAction.fetchProfileData, ({ state, commit }, userIds) => {
        const cpp = state.chatParticipantProfiles;
        userIds = userIds.filter(userId => !cpp[userId] || cpp[userId].loadState === 'FAILED');

        if (!userIds.length) {
            return;
        }

        commit(storeMutation.setParticipantState, { userIds, loadState: 'PENDING' });

        return axios({
            method: 'POST',
            url: `${state.baseURL}chat/api/chat/get-chat-users`,
            headers: getBasicHeaders(),
            data: { userIds },
        })
            .then((response) => {
                let chatUsers = {};
                response.data.chatUsers.forEach((profile) => {
                    chatUsers = {
                        ...chatUsers,
                        [profile.id]: {
                            userId: profile.id,
                            loadState: 'LOADED',
                            id: profile.id,
                            name: [profile.preferredName, profile.lastName].filter(x => x && x.length).join(' '),
                            profileImageUrl: profile.profileImageUrl,
                            fallbackProfileImageUrl: profile.fallbackProfileImageUrl,
                            primaryChapterName: profile.chapterName,
                            primaryChapterId: profile.chapterId,
                            companyName: profile.companyName,
                        },
                    };
                });
                commit(storeMutation.mergeChatParticipantProfile, chatUsers);
            })
            .catch((err) => {
                commit(storeMutation.setParticipantState, { userIds, loadState: 'FAILED' });
                throw err;
            });
    }),
};

const messengerGetters = {
    [storeGetters.messengerSessionState]: state => state.messengerSessionState,
    [storeGetters.messengerUsername]: state => state.messengerUsername && state.messengerUsername.toLowerCase(),
    [storeGetters.chatParticipantProfiles]: state => state.chatParticipantProfiles,
    [storeGetters.conversations]: (state, getters, rootState, rootGetters) => {
        const currentUserId = rootGetters[`memberProfile/${storeGetters.currentUserId}`];
        const conversations = state.rooms
            .map((room) => {
                const participants = getRoomParticipants(state, room);

                const events = getEvents(state, room);

                return {
                    id: room.roomId,
                    name: room.name,
                    room,
                    participants,
                    events,
                };
            })
            .filter(c => c.participants[currentUserId && currentUserId.toLowerCase()]);

        const lastEventTs = (x) => {
            if (x.events && x.events.length) {
                const messageEvents = x.events.filter(e => e.type && e.type === 'message');
                return messageEvents.length ? messageEvents[messageEvents.length - 1].ts : Number.MIN_VALUE;
            }
            return Number.MIN_VALUE;
        };
        conversations.sort((a, b) => lastEventTs(b) - lastEventTs(a));

        return conversations;
    },
    [storeGetters.globalChatNotificationCount]: (state) => {
        return state.globalChatNotificationCount;
    },

};

export default {
    namespaced: true,
    state: {
        initialState: messengerState(),
        ...messengerState(),
    },
    mutations: { ...messengerMutations },
    actions: { ...messengerActions },
    getters: { ...messengerGetters },
};
